import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import { Incident, IncidentComment, IncidentStatus } from '@/types';
import { deleteIncidentAttachmentsStorage } from '@/services/incidentAttachmentService';
import { nowWithTimezone } from '@/utils/timezone';

type DbIncident = {
  id: string;
  title: string;
  description?: string | null;
  status?: string | null;
  created_at: string;
  community_id: string;
  created_by: string;
  comments?: unknown;
};

type InsertIncident = {
  title: string;
  description?: string | null;
  status?: string | null;
  community_id: string;
  created_by: string;
  created_at: string;
};

const INCIDENT_SELECT =
  'id, title, description, status, created_at, community_id, created_by, comments';

const normalizeIncidentComments = (value: unknown): IncidentComment[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(item => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const { role, fechaHora, texto } = item as IncidentComment;
      const legacyInitials = (item as { iniciales?: unknown }).iniciales;
      const resolvedRole = typeof role === 'string' ? role : legacyInitials;
      if (
        typeof resolvedRole !== 'string' ||
        typeof fechaHora !== 'string' ||
        typeof texto !== 'string'
      ) {
        return null;
      }

      return { role: resolvedRole, fechaHora, texto };
    })
    .filter((item): item is IncidentComment => item !== null);
};

const fromDbStatus = (status?: string | null): IncidentStatus => {
  if (!status) return 'pending';
  const normalized = status.toLowerCase();
  switch (normalized) {
    case 'pending':
      return 'pending';
    case 'in-progress':
    case 'in_progress':
      return 'in-progress';
    case 'resolved':
    case 'closed':
      return 'resolved';
    case 'discarded':
    case 'discardada':
    case 'descartada':
      return 'discarded';
    default:
      return 'pending';
  }
};

const toDbStatus = (status: IncidentStatus | undefined): string | undefined => {
  if (!status) return undefined;
  if (status === 'in-progress') return 'in_progress';
  return status;
};

function mapDbIncident(row: DbIncident): Incident {
  const reporterInitials: string | undefined = undefined;
  const comments = normalizeIncidentComments(row.comments);

  return {
    id: row.id,
    title: row.title,
    description: row.description ?? '',
    status: fromDbStatus(row.status),
    createdAt: row.created_at,
    updatedAt: row.created_at,
    communityId: row.community_id,
    reportedById: row.created_by,
    reportedByName: reporterInitials,
    images: [],
    comments,
  };
}

export async function getIncidentsByCommunity(communityId: string): Promise<Incident[]> {
  await ensureAuthenticatedSession();
  const { data, error } = await supabase
    .from('incidents')
    .select(INCIDENT_SELECT)
    .eq('community_id', communityId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapDbIncident);
}

export async function getAllIncidents(): Promise<Incident[]> {
  await ensureAuthenticatedSession();
  const { data, error } = await supabase
    .from('incidents')
    .select(INCIDENT_SELECT)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapDbIncident);
}

interface CreateIncidentInput {
  title: string;
  description: string;
  communityId: string;
  createdBy: string;
  status?: IncidentStatus;
}

export async function createIncidentRecord(input: CreateIncidentInput): Promise<Incident> {
  await ensureAuthenticatedSession();
  const payload: InsertIncident = {
    title: input.title,
    description: input.description,
    community_id: input.communityId,
    created_by: input.createdBy,
    status: toDbStatus(input.status ?? 'pending'),
    created_at: nowWithTimezone(),
  };

  const { data, error } = await supabase
    .from('incidents')
    .insert([payload])
    .select(INCIDENT_SELECT)
    .single();

  if (error) throw error;
  if (!data) {
    throw new Error('No se pudo crear la incidencia.');
  }
  return mapDbIncident(data);
}

export async function updateIncidentStatusRecord(id: string, status: IncidentStatus): Promise<Incident> {
  await ensureAuthenticatedSession();
  const { data, error } = await supabase
    .from('incidents')
    .update({ status: toDbStatus(status) })
    .eq('id', id)
    .select(INCIDENT_SELECT)
    .single();

  if (error) throw error;
  if (!data) {
    throw new Error('No se encontró la incidencia para actualizar.');
  }
  const mapped = mapDbIncident(data);
  return { ...mapped, updatedAt: new Date().toISOString() };
}

interface UpdateIncidentInput {
  title: string;
  description: string;
  status: IncidentStatus;
  communityId: string;
}

export async function updateIncidentRecord(id: string, input: UpdateIncidentInput): Promise<Incident> {
  await ensureAuthenticatedSession();
  const { data, error } = await supabase
    .from('incidents')
    .update({
      title: input.title,
      description: input.description,
      status: toDbStatus(input.status),
      community_id: input.communityId,
    })
    .eq('id', id)
    .select(INCIDENT_SELECT)
    .single();

  if (error) throw error;
  if (!data) {
    throw new Error('No se encontró la incidencia para actualizar.');
  }

  const mapped = mapDbIncident(data);
  return { ...mapped, updatedAt: new Date().toISOString() };
}

export async function addIncidentComment(
  id: string,
  comment: IncidentComment,
): Promise<Incident> {
  await ensureAuthenticatedSession();
  const { data: currentData, error: currentError } = await supabase
    .from('incidents')
    .select('comments')
    .eq('id', id)
    .single();

  if (currentError) throw currentError;

  const existingComments = normalizeIncidentComments(currentData?.comments);
  const updatedComments = [...existingComments, comment];

  const { data, error } = await supabase
    .from('incidents')
    .update({ comments: updatedComments })
    .eq('id', id)
    .select(INCIDENT_SELECT)
    .single();

  if (error) throw error;
  if (!data) {
    throw new Error('No se pudo guardar el comentario.');
  }

  return mapDbIncident(data);
}

export async function removeIncidentComment(
  id: string,
  index: number,
): Promise<Incident> {
  await ensureAuthenticatedSession();
  const { data: currentData, error: currentError } = await supabase
    .from('incidents')
    .select('comments')
    .eq('id', id)
    .single();

  if (currentError) throw currentError;

  const existingComments = normalizeIncidentComments(currentData?.comments);
  if (index < 0 || index >= existingComments.length) {
    throw new Error('No se encontró el comentario a eliminar.');
  }

  const updatedComments = existingComments.filter((_, idx) => idx !== index);

  const { data, error } = await supabase
    .from('incidents')
    .update({ comments: updatedComments })
    .eq('id', id)
    .select(INCIDENT_SELECT)
    .single();

  if (error) throw error;
  if (!data) {
    throw new Error('No se pudo eliminar el comentario.');
  }

  return mapDbIncident(data);
}

export async function deleteIncidentRecord(id: string, userId: string, communityId?: string): Promise<void> {
  await ensureAuthenticatedSession();
  if (communityId) {
    await deleteIncidentAttachmentsStorage(communityId, id).catch(() => {});
  }
  const pendingStatus = toDbStatus('pending') ?? 'pending';

  const { data, error } = await supabase
    .from('incidents')
    .delete()
    .match({ id, created_by: userId, status: pendingStatus })
    .select('id');

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('No se encontró la incidencia o no tienes permisos para eliminarla.');
  }
}

export async function forceDeleteIncidentRecord(id: string, communityId?: string): Promise<void> {
  await ensureAuthenticatedSession();
  if (communityId) {
    await deleteIncidentAttachmentsStorage(communityId, id).catch(() => {});
  }
  const { error } = await supabase
    .from('incidents')
    .delete()
    .eq('id', id);

  if (error) throw error;
}
